import React, { useState } from 'react';
import useContentResources from '../hooks/useContentResources';
import useTargetLocales from '../hooks/useTargetLocales';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../components/ui/table';
import { ChevronRight, ExternalLink, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import SyncModal from '../components/SyncModal';
import { localeLabel } from '../utils/localeFlag';

// Build a Shopify Admin URL for an Article or Page GID. Mirrors the Phase 15
// pattern from Collections.jsx (single-tenant store: lamptwist-live), but
// branches by resource_type so the same component serves both /articles and
// /pages-content routes (D-06 unified component + Pitfall 2 surface for the
// /articles vs /pages admin paths).
function shopifyAdminUrl(resourceId, resourceType) {
  const id = resourceId ? resourceId.split('/').pop() : '';
  if (resourceType === 'ARTICLE') {
    return `https://admin.shopify.com/store/lamptwist-live/articles/${id}`;
  }
  return `https://admin.shopify.com/store/lamptwist-live/pages/${id}`;
}

// Phase-18 D-08 / Pitfall 2 surface in UI: PAGE resources have no
// summary_html field on Shopify's translatableResource — the field key option
// is gated to ARTICLE only. Backend already enforces this at the data layer.
const PAGE_FIELD_KEY_OPTIONS = [
  { id: '', label: 'All fields' },
  { id: 'title', label: 'Title' },
  { id: 'body_html', label: 'Body HTML' },
  { id: 'handle', label: 'Handle' },
  { id: 'meta_title', label: 'Meta title' },
  { id: 'meta_description', label: 'Meta description' },
];

const ARTICLE_FIELD_KEY_OPTIONS = [
  { id: '', label: 'All fields' },
  { id: 'title', label: 'Title' },
  { id: 'body_html', label: 'Body HTML' },
  { id: 'summary_html', label: 'Summary HTML' },          // ARTICLE-only — Pitfall 2 surface
  { id: 'handle', label: 'Handle' },
  { id: 'meta_title', label: 'Meta title' },
  { id: 'meta_description', label: 'Meta description' },
];

const POLICY_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'translate', label: 'Translate' },
  { id: 'skip', label: 'Skip (user)' },
  { id: 'auto-skipped', label: 'Auto-skipped' },
];

const STATUS_FILTERS_ARTICLE = [
  { id: 'all', label: 'All articles' },
  { id: 'missing', label: 'Missing translations only' },
];

const STATUS_FILTERS_PAGE = [
  { id: 'all', label: 'All pages' },
  { id: 'missing', label: 'Missing translations only' },
];

// Group key = leading title segment (before the first ' > ').
// Articles/pages rarely use the ' > ' convention, but preserve the helper
// for parity with Collections.jsx so prefix grouping degrades gracefully into
// "one prefix == one resource" when no titles have separators.
function groupKeyForTitle(title) {
  if (!title) return '—';
  const idx = title.indexOf(' > ');
  return idx === -1 ? title : title.slice(0, idx);
}

// Per-row status badge (used in the expanded per-field view)
function StatusBadge({ status }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const map = {
    pending: { className: 'bg-gray-100 text-gray-800 hover:bg-gray-100', label: 'Pending' },
    translated: { className: 'bg-blue-100 text-blue-800 hover:bg-blue-100', label: 'Translated' },
    pushed: { className: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100', label: 'Pushed' },
    failed: { className: 'bg-red-100 text-red-800 hover:bg-red-100', label: 'Failed' },
  };
  const cfg = map[status] || { className: '', label: status };
  return <Badge className={cfg.className}>{cfg.label}</Badge>;
}

// A row "needs translation" iff there's something to translate AND its
// effective policy resolves to translate. Empty source is the first gate:
// the sync's preserve-user-override CASE can leave a row marked
// translation_policy='translate' after the merchant clears its value in
// Shopify, so policy alone isn't enough to know whether work is pending.
// Same policy precedence as effectivePolicyValue: field default → row
// override → auto_skip_reason → translate-by-default.
function needsTranslation(row) {
  if (!row.raw_value) return false;
  const effective = row.effective_policy;
  if (effective === 'skip') return false;
  if (effective === 'translate') return true;
  if (row.auto_skip_reason) return false;
  return true;
}

// Roll up a resource's fields into a per-locale status for the parent row.
function rollupForLocale(fields, locale) {
  const statusKey = `${locale}_status`;
  let needed = 0, pushed = 0, failed = 0;
  for (const f of fields) {
    if (!needsTranslation(f)) continue;
    needed++;
    if (f[statusKey] === 'pushed') pushed++;
    else if (f[statusKey] === 'failed') failed++;
  }
  return { needed, pushed, failed };
}

function RollupBadge({ rollup }) {
  const { needed, pushed, failed } = rollup;
  if (needed === 0) {
    return <span className="text-muted-foreground text-xs">—</span>;
  }
  if (failed > 0) {
    return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{pushed}/{needed} · {failed} failed</Badge>;
  }
  if (pushed === needed) {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{pushed}/{needed}</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{pushed}/{needed}</Badge>;
}

function truncate(value, n = 80) {
  if (!value) return '';
  return value.length > n ? value.slice(0, n) + '…' : value;
}

// Resolve the effective policy a row will be translated under. Precedence is
// field default → row override → auto_skip_reason → 'auto'. Explicit operator
// choice wins over auto-classification — a row with both auto_skip_reason
// and field_default_policy displays the Settings value.
function effectivePolicyValue(row) {
  if (row.effective_policy) return row.effective_policy;
  if (row.auto_skip_reason) return 'auto';
  return 'auto';
}

function PolicyBadge({ row }) {
  const value = effectivePolicyValue(row);
  const cls = value === 'translate'
    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300'
    : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded capitalize ${cls}`}>
      {value}
    </span>
  );
}

function FieldDefaultHint({ row }) {
  if (!row.field_default_policy) return null;
  return (
    <span className="block text-[10px] text-muted-foreground mt-1">
      from default
    </span>
  );
}

/**
 * Unified Articles + Pages inventory page (Phase 18, Plan 18-03).
 *
 * One component, two routes — wired in App.jsx behind /articles and
 * /pages-content with resourceType='ARTICLE' and resourceType='PAGE'
 * respectively (D-06). Page chrome, FIELD_KEY filter options, and the
 * Shopify Admin URL helper all adapt to the prop.
 *
 * Inline helpers (StatusBadge, RollupBadge, truncate, needsTranslation,
 * rollupForLocale, groupKeyForTitle, PolicyBadge, FieldDefaultHint) are
 * duplicated verbatim from Collections.jsx + ProductResourceDetail.jsx —
 * Phase 17/18 UI-SPEC "preserve inline duplication" precedent; do NOT
 * refactor to a shared module. The policy column is read-only; editing
 * lives in Settings → Field Defaults.
 *
 * SyncModal wiring is D-25: clicking sync from either route triggers the
 * same dual-walk job (the backend doesn't fan-out by which page invoked
 * it). Title says "Articles & Pages" so the operator knows both refresh
 * together regardless of where they clicked from.
 */
export default function ContentResources({ resourceType }) {
  const isArticle = resourceType === 'ARTICLE';
  const pageTitle = isArticle ? 'Articles' : 'Pages';
  const singularLabel = isArticle ? 'article' : 'page';
  const pluralLabel = isArticle ? 'articles' : 'pages';
  const fieldKeyOptions = isArticle ? ARTICLE_FIELD_KEY_OPTIONS : PAGE_FIELD_KEY_OPTIONS;
  const statusFilters = isArticle ? STATUS_FILTERS_ARTICLE : STATUS_FILTERS_PAGE;

  const {
    rows,
    total,
    loading,
    error,
    fieldKey,
    setFieldKey,
    policy,
    setPolicy,
    hasHtml,
    setHasHtml,
    status,
    setStatus,
    q,
    setQ,
    page,
    setPage,
    pageSize,
    syncModalOpen,
    setSyncModalOpen,
    lastSynced,
    fetchData,
  } = useContentResources(resourceType);

  const { locales } = useTargetLocales();

  const [expanded, setExpanded] = useState(() => new Set());
  const [expandedPrefixes, setExpandedPrefixes] = useState(() => new Set());

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleExpanded = (resourceId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  };

  const togglePrefix = (prefix) => {
    setExpandedPrefixes((prev) => {
      const next = new Set(prev);
      if (next.has(prefix)) next.delete(prefix);
      else next.add(prefix);
      return next;
    });
  };

  const handleSyncClose = () => {
    setSyncModalOpen(false);
    fetchData();
  };

  // Group rows by resource_id, preserving server-side order.
  const groupOrder = [];
  const groups = {};
  rows.forEach((r) => {
    if (!groups[r.resource_id]) {
      groups[r.resource_id] = {
        title: r.title,
        // Articles/pages don't surface a separate `handle` field in the row,
        // but if the inventory has a 'handle' field_key row we surface the
        // raw_value below; for the parent row we just show the gid leaf.
        fields: [],
        resourceType: r.resource_type,
      };
      groupOrder.push(r.resource_id);
    }
    groups[r.resource_id].fields.push(r);
  });

  // Compute first-segment prefix groups for visual headers + aggregate
  // rollups. Order preserved because the server returns rows pre-sorted.
  const prefixOrder = [];
  const prefixCounts = {};
  const prefixMembers = {};
  const prefixRollups = {};
  groupOrder.forEach((resourceId) => {
    const key = groupKeyForTitle(groups[resourceId].title);
    if (!(key in prefixCounts)) {
      prefixCounts[key] = 0;
      prefixMembers[key] = [];
      prefixRollups[key] = {};
      for (const loc of locales) {
        prefixRollups[key][loc] = { needed: 0, pushed: 0, failed: 0 };
      }
      prefixOrder.push(key);
    }
    prefixCounts[key] += 1;
    prefixMembers[key].push(resourceId);
    for (const loc of locales) {
      const r = rollupForLocale(groups[resourceId].fields, loc);
      prefixRollups[key][loc].needed += r.needed;
      prefixRollups[key][loc].pushed += r.pushed;
      prefixRollups[key][loc].failed += r.failed;
    }
  });

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-2xl font-bold text-foreground">{pageTitle}</h1>
        <Button onClick={() => setSyncModalOpen(true)}>Sync from Shopify</Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {lastSynced ? `Last synced: ${new Date(lastSynced).toLocaleString()}` : 'Never synced'}
        {!loading && ` · ${groupOrder.length.toLocaleString()} ${pluralLabel} · ${prefixOrder.length.toLocaleString()} group(s)`}
        {!loading && status === 'missing' && ` · filtered to ${pluralLabel} with at least one pending/failed translation`}
      </p>

      {/* Heads-up: storefront-looks-translated ≠ this view shows translated.
          Theme strings and article/page body content are separate translation
          surfaces in Shopify and live in two different Transly pipelines. */}
      <div className="rounded-md border border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-100 p-3 mb-6 text-sm flex gap-2 items-start">
        <Info className="h-4 w-4 mt-0.5 shrink-0" />
        <div>
          <strong>Heads up:</strong> this view tracks {pluralLabel.toLowerCase()} <em>body content</em> — the
          actual title and HTML body of each {pageTitle.slice(0, -1).toLowerCase()}.
          The storefront UI chrome around that content (menus, buttons, section headings) is
          translated separately as <Link to="/theme-strings" className="underline font-medium">theme strings</Link>.
          A {pageTitle.slice(0, -1).toLowerCase()} can look translated on the storefront because its
          theme strings are translated, even when this view correctly shows <code>0/2</code> here.
          Translate one surface or the other (or both) depending on what's actually missing.
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={fieldKey}
          onChange={(e) => setFieldKey(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          {fieldKeyOptions.map((o) => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>

        <div className="flex gap-1">
          {statusFilters.map((f) => (
            <Button
              key={f.id}
              variant={status === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatus(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <div className="flex gap-1">
          {POLICY_FILTERS.map((f) => (
            <Button
              key={f.id}
              variant={policy === f.id ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setPolicy(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        <select
          value={hasHtml}
          onChange={(e) => setHasHtml(e.target.value)}
          className="border border-input bg-background h-9 rounded-md px-2 text-sm"
        >
          <option value="">All types</option>
          <option value="true">HTML only</option>
          <option value="false">Plain text only</option>
        </select>

        <Input
          type="text"
          placeholder={`Search ${singularLabel} title, handle or value...`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="flex-1 min-w-[200px]"
        />
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 text-red-800 p-3 mb-4 text-sm">
          {error}
        </div>
      )}

      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {!loading && rows.length === 0 && (
        <div className="border rounded p-8 text-center text-muted-foreground">
          No {singularLabel} rows match the current filters. Try a sync if the table is empty.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{isArticle ? 'Article' : 'Page'}</TableHead>
                {locales.map((loc) => (
                  <TableHead key={loc} className="w-28">{localeLabel(loc)}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {prefixOrder.map((prefix) => {
                const prefixOpen = expandedPrefixes.has(prefix);
                return (
                  <React.Fragment key={prefix}>
                    <TableRow
                      className="bg-muted hover:bg-muted/80 cursor-pointer"
                      onClick={() => togglePrefix(prefix)}
                    >
                      <TableCell className="py-2">
                        <div className="flex items-center gap-2">
                          <ChevronRight
                            className={`h-4 w-4 transition-transform ${prefixOpen ? 'rotate-90' : ''}`}
                          />
                          <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                            {prefix}
                          </span>
                          <span className="text-xs text-muted-foreground font-normal">
                            · {prefixCounts[prefix]} {prefixCounts[prefix] === 1 ? singularLabel : pluralLabel}
                          </span>
                        </div>
                      </TableCell>
                      {locales.map((loc) => (
                        <TableCell key={loc}>
                          <RollupBadge rollup={prefixRollups[prefix][loc]} />
                        </TableCell>
                      ))}
                    </TableRow>
                    {prefixOpen && prefixMembers[prefix].map((resourceId) => {
                      const g = groups[resourceId];
                      const isOpen = expanded.has(resourceId);
                      const localeRollups = Object.fromEntries(
                        locales.map((loc) => [loc, rollupForLocale(g.fields, loc)]),
                      );
                      // Hide the prefix in the row label when the header
                      // already names it. Mirrors Collections.jsx behaviour.
                      const leafLabel = g.title && g.title.includes(' > ')
                        ? g.title.slice(g.title.indexOf(' > ') + 3)
                        : g.title;
                      return (
                        <React.Fragment key={resourceId}>
                          <TableRow
                            className="cursor-pointer hover:bg-accent"
                            onClick={() => toggleExpanded(resourceId)}
                          >
                            <TableCell>
                              <div className="flex items-center gap-2 pl-6">
                                <ChevronRight
                                  className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-90' : ''}`}
                                />
                                <span className="font-medium">{leafLabel || resourceId}</span>
                                <a
                                  href={shopifyAdminUrl(resourceId, g.resourceType || resourceType)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  title="Open in Shopify Admin"
                                  className="text-muted-foreground hover:text-foreground inline-flex items-center"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              </div>
                            </TableCell>
                            {locales.map((loc) => (
                              <TableCell key={loc}>
                                <RollupBadge rollup={localeRollups[loc]} />
                              </TableCell>
                            ))}
                          </TableRow>
                          {isOpen && (
                            <TableRow className="bg-muted/30">
                              <TableCell colSpan={locales.length + 1} className="p-0">
                                <table className="w-full">
                                  <thead>
                                    <tr className="text-xs text-muted-foreground border-b">
                                      <th className="text-left font-normal pl-10 py-2 w-40">Field</th>
                                      <th className="text-left font-normal py-2">Value</th>
                                      <th className="text-left font-normal py-2 w-32">Policy</th>
                                      {locales.map((loc) => (
                                        <th key={loc} className="text-left font-normal py-2 w-28">{localeLabel(loc)}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {g.fields.map((row) => (
                                      <tr key={row.id} className="border-b last:border-b-0">
                                        <td className="pl-10 py-2 text-xs font-mono">{row.field_key}</td>
                                        <td className="py-2 text-sm max-w-md truncate pr-4" title={row.raw_value || ''}>
                                          {truncate(row.raw_value, 80)}
                                        </td>
                                        <td className="py-2">
                                          <div className="flex items-center gap-2">
                                            <div>
                                              <PolicyBadge row={row} />
                                              <FieldDefaultHint row={row} />
                                            </div>
                                            {row.field_key === 'handle' && effectivePolicyValue(row) === 'translate' && (
                                              <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                                                URL slug
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                        {locales.map((loc) => (
                                          <td key={loc} className="py-2"><StatusBadge status={row[`${loc}_status`]} /></td>
                                        ))}
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-muted-foreground">
              Page {page} of {totalPages} · {total.toLocaleString()} total rows
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      <SyncModal
        open={syncModalOpen}
        onClose={handleSyncClose}
        endpoint="/api/transly/content-resources/sync/"
        statusEndpoint="/api/transly/content-resources/sync-status/"
        title="Syncing Articles & Pages"
      />
    </div>
  );
}
